import { useEffect, useMemo, useRef, useState } from "react";
import Scene1 from "./scenes/Scene1";
import Scene3 from "./scenes/Scene3";
import Scene4 from "./scenes/Scene4";
import Scene7 from "./scenes/Scene7";
import Scene5 from "./scenes/Scene5";
import Scene6 from "./scenes/Scene6";
import { SCENE_IMAGES, SCENE_IMAGES_2X, SCENE_IMAGES_MOBILE, SCENE_IMAGES_MOBILE_2X, SCROLL_CONFIG } from "./sceneAssets";
import { getSceneCopy } from "./sceneCopy";
import { useCinematicTimeline } from "./useCinematicTimeline";
import "./LogisticsStory.css";

const TABLET_MAX = 1024;
const MOBILE_MAX = 720;

function getBreakpoint() {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < MOBILE_MAX) return "mobile";
  if (w < TABLET_MAX) return "tablet";
  return "desktop";
}

function prefersReducedMotion() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function syncHeaderHeight() {
  const header = document.querySelector("header");
  const h = header?.offsetHeight ?? 82;
  document.documentElement.style.setProperty("--app-header-h", `${h}px`);
}

function preloadSceneImages(breakpoint) {
  Object.values(SCENE_IMAGES).forEach((src) => {
    const img = new Image();
    img.src = src;
  });

  if (breakpoint === "mobile") {
    [...Object.values(SCENE_IMAGES_MOBILE), ...Object.values(SCENE_IMAGES_MOBILE_2X)].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    return;
  }

  if (typeof window !== "undefined" && window.devicePixelRatio >= 1.5) {
    Object.values(SCENE_IMAGES_2X).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }
}

/**
 * Cinematic landing hero — image-driven scroll journey.
 * Swap files in public/assets/logistics-story/scenes/ to upgrade artwork.
 */
export default function LogisticsStory({ language = "en" }) {
  const isArabic = language === "ar";
  const copy = useMemo(() => getSceneCopy(language), [language]);

  const rootRef = useRef(null);
  const pinRef = useRef(null);
  const sceneRefs = useRef({});

  const setSceneRef = (id) => (node) => {
    sceneRefs.current[id] = node;
  };

  const [breakpoint, setBreakpoint] = useState(getBreakpoint);
  const [reduced, setReduced] = useState(prefersReducedMotion);

  useEffect(() => {
    syncHeaderHeight();
    preloadSceneImages(getBreakpoint());

    const onResize = () => {
      syncHeaderHeight();
      setBreakpoint(getBreakpoint());
    };

    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onMotion = () => setReduced(mq.matches);

    window.addEventListener("resize", onResize);
    mq.addEventListener?.("change", onMotion);
    mq.addListener?.(onMotion);

    return () => {
      window.removeEventListener("resize", onResize);
      mq.removeEventListener?.("change", onMotion);
      mq.removeListener?.(onMotion);
    };
  }, []);

  const scrollCfg = SCROLL_CONFIG[breakpoint] || SCROLL_CONFIG.desktop;
  const animated = !reduced;

  useCinematicTimeline({
    enabled: animated,
    rootRef,
    pinRef,
    sceneRefs,
    totalVh: scrollCfg.totalVh,
    slowScroll: breakpoint === "mobile",
  });

  const layoutClass = [
    "logistics-story",
    "logistics-story--cinematic",
    reduced ? "logistics-story--reduced" : "",
    breakpoint === "mobile" ? "logistics-story--mobile" : "",
    breakpoint === "tablet" ? "logistics-story--tablet" : "",
    isArabic ? "logistics-story--lang-ar" : "logistics-story--lang-en",
  ]
    .filter(Boolean)
    .join(" ");

  const shared = { language, isArabic, breakpoint };

  if (reduced) {
    return (
      <section
        className={layoutClass}
        aria-label={isArabic ? "قصة كارغوكوت اللوجستية" : "QargoQuote logistics story"}
      >
        <div className="logistics-story__pin">
          <div className="logistics-story__stage">
            <Scene6 ref={setSceneRef(6)} copy={copy[6]} {...shared} />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={rootRef}
      className={layoutClass}
      aria-label={isArabic ? "قصة كارغوكوت اللوجستية" : "QargoQuote logistics story"}
    >
      <div ref={pinRef} className="logistics-story__pin">
        <div className="logistics-story__stage">
          <Scene1 ref={setSceneRef(1)} copy={copy[1]} {...shared} />
          <Scene3 ref={setSceneRef(3)} copy={copy[3]} {...shared} />
          <Scene4 ref={setSceneRef(4)} copy={copy[4]} {...shared} />
          <Scene7 ref={setSceneRef(7)} copy={copy[7]} {...shared} />
          <Scene5 ref={setSceneRef(5)} copy={copy[5]} {...shared} />
          <Scene6 ref={setSceneRef(6)} copy={copy[6]} {...shared} />
        </div>
      </div>
    </section>
  );
}
